import { Box, InputBase } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { ReactElement, Ref, useState } from 'react';
import { tokens } from '@synergy/theme-palo';

const c = tokens.color;

export interface StreamReplyProps {
    onSubmit: (text: string) => Promise<void>;
    /** Defaults to the reply wording used on an entry. */
    placeholderId?: string;
    submitId?: string;
    inputRef?: Ref<HTMLInputElement>;
    /** Set when an existing remark is being changed rather than written. */
    initialText?: string;
    autoFocus?: boolean;
    /** Offered alongside the send button when there is something to go back to. */
    onCancel?: () => void;
}

/**
 * The one-line field a remark is written in (#2154, #2156).
 *
 * A single row, the height of a list row — the study puts one of these on every
 * entry, and a two-line text box repeated down the stream would swallow it. The
 * button only appears once there is something to send, so an untouched stream
 * stays quiet.
 *
 * Enter sends; Shift+Enter is deliberately not a line break here, because this
 * field holds a sentence, not a letter.
 */
export const StreamReply = ({
    onSubmit,
    placeholderId,
    submitId,
    inputRef,
    initialText,
    autoFocus,
    onCancel,
}: StreamReplyProps): ReactElement => {
    const intl = useIntl();
    const [text, setText] = useState(initialText ?? '');
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        const value = text.trim();

        if (!value || busy) {
            return;
        }

        setBusy(true);
        await onSubmit(value);
        setBusy(false);
        // Editing ends by closing the field; clearing it here would blank the
        // remark for the moment before the parent unmounts it.
        if (undefined === initialText) {
            setText('');
        }
    };

    return (
        <Box sx={{ marginTop: '7px', display: 'flex', gap: '6px' }}>
            <InputBase
                value={text}
                inputRef={inputRef}
                disabled={busy}
                autoFocus={autoFocus}
                // Autofocus alone leaves the caret in front of the text, so
                // typing would prepend to what is already written.
                onFocus={event => event.target.setSelectionRange(text.length, text.length)}
                onChange={event => setText(event.target.value)}
                onKeyDown={event => {
                    if ('Enter' === event.key && !event.shiftKey) {
                        event.preventDefault();
                        submit();
                    }

                    if ('Escape' === event.key && onCancel) {
                        event.preventDefault();
                        onCancel();
                    }
                }}
                placeholder={intl.formatMessage({ id: placeholderId ?? 'app.stream.reply.placeholder' })}
                sx={{
                    flex: '1 1 0',
                    height: 26,
                    padding: '0 9px',
                    fontSize: '12px',
                    color: c.ink,
                    backgroundColor: c.surface,
                    border: `1px solid ${c.lineHard}`,
                    borderRadius: tokens.radius.small + 'px',
                    '&.Mui-focused': {
                        borderColor: c.accent,
                        boxShadow: `0 0 0 2px ${c.accentSoft}`,
                    },
                }}
            />
            {'' !== text.trim() && (
                <Box
                    component="button"
                    type="button"
                    disabled={busy}
                    onClick={submit}
                    sx={{
                        flexShrink: 0,
                        height: 26,
                        padding: '0 11px',
                        fontSize: '11.5px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        color: c.inkInvert,
                        backgroundColor: c.accent,
                        border: `1px solid ${c.accent}`,
                        borderRadius: tokens.radius.small + 'px',
                        '&:hover': { backgroundColor: c.accentHi },
                        '&:disabled': { opacity: 0.6, cursor: 'default' },
                    }}
                >
                    <FormattedMessage id={submitId ?? 'app.stream.reply.submit'} />
                </Box>
            )}
            {onCancel && (
                <Box
                    component="button"
                    type="button"
                    onClick={onCancel}
                    sx={{
                        flexShrink: 0,
                        height: 26,
                        padding: '0 11px',
                        fontSize: '11.5px',
                        cursor: 'pointer',
                        color: c.ink2,
                        backgroundColor: c.surface,
                        border: `1px solid ${c.lineHard}`,
                        borderRadius: tokens.radius.small + 'px',
                    }}
                >
                    <FormattedMessage id="app.common.cancel" />
                </Box>
            )}
        </Box>
    );
};
